import React, { useEffect, useState, useRef, useCallback } from 'react';

import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { AutoComplete } from 'primereact/autocomplete';
import { Dropdown } from 'primereact/dropdown';

import debounce from 'lodash/debounce';

import axiosInstance from '../../config/axiosConfig';

import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';

countries.registerLocale(en);

// A small helper array or function to determine if a country is in Europe or has a VAT system.
const europeanCountries = [
    'France', 'Germany', 'Spain', 'Italy', 'Belgium', 'Netherlands', 'Luxembourg',
    'Ireland', 'Portugal', 'Austria', 'Greece', 'Finland', 'Sweden', 'Denmark',
    'Poland', 'Czech Republic', 'Slovakia', 'Hungary', 'Romania', 'Bulgaria',
    'Croatia', 'Slovenia', 'Estonia', 'Latvia', 'Lithuania', 'Cyprus', 'Malta',
    'Norway', 'Switzerland', 'Iceland', 'Liechtenstein'
];

function isEuropeanCountry(countryName) {
    return europeanCountries.map(c => c.toLowerCase()).includes(countryName.toLowerCase());
}

// Legal forms depending on country
const legalFormsByCountry = {
    France: [
        { label: 'SARL', value: 'SARL' },
        { label: 'SAS', value: 'SAS' },
        { label: 'SASU', value: 'SASU' },
        { label: 'SCI', value: 'SCI' }
    ],
    Germany: [
        { label: 'GmbH', value: 'GmbH' },
        { label: 'UG (haftungsbeschränkt)', value: 'UG' },
        { label: 'AG', value: 'AG' },
        { label: 'OHG', value: 'OHG' }
    ],
    Spain: [
        { label: 'SL', value: 'SL' },
        { label: 'SA', value: 'SA' },
        { label: 'SC', value: 'SC' },
        { label: 'SCoop', value: 'SCoop' }
    ],
    Italy: [
        { label: 'SRL', value: 'SRL' },
        { label: 'SPA', value: 'SPA' },
        { label: 'SNC', value: 'SNC' },
        { label: 'SAS', value: 'SAS' }
    ],
    Belgium: [
        { label: 'SRL', value: 'SRL' },
        { label: 'SA', value: 'SA' },
        { label: 'SNC', value: 'SNC' },
        { label: 'SC', value: 'SC' }
    ],
    Netherlands: [
        { label: 'BV', value: 'BV' },
        { label: 'NV', value: 'NV' },
        { label: 'VOF', value: 'VOF' },
        { label: 'Maatschap', value: 'Maatschap' }
    ],
    Luxembourg: [
        { label: 'SARL', value: 'SARL' },
        { label: 'SA', value: 'SA' },
        { label: 'SCS', value: 'SCS' },
        { label: 'SCA', value: 'SCA' }
    ],
    Ireland: [
        { label: 'Private Company Limited by Shares (LTD)', value: 'LTD' },
        { label: 'Designated Activity Company (DAC)', value: 'DAC' },
        { label: 'Public Limited Company (PLC)', value: 'PLC' },
        { label: 'Unlimited Company (UC)', value: 'UC' }
    ],
    Switzerland: [
        { label: 'GmbH', value: 'GmbH' },
        { label: 'AG', value: 'AG' },
        { label: 'Kollektivgesellschaft', value: 'Kollektivgesellschaft' },
        { label: 'Kommanditgesellschaft', value: 'Kommanditgesellschaft' }
    ],
    Austria: [
        { label: 'GmbH', value: 'GmbH' },
        { label: 'AG', value: 'AG' },
        { label: 'OG', value: 'OG' },
        { label: 'KG', value: 'KG' }
    ],
    UnitedStates: [
        { label: 'Corporation', value: 'Corporation' },
        { label: 'LLC', value: 'LLC' },
        { label: 'Partnership', value: 'Partnership' },
        { label: 'Sole Proprietorship', value: 'Sole Proprietorship' }
    ],
    Canada: [
        { label: 'Corporation', value: 'Corporation' },
        { label: 'Sole Proprietorship', value: 'Sole Proprietorship' },
        { label: 'Partnership', value: 'Partnership' },
        { label: 'Co-operative', value: 'Co-operative' }
    ],
    Australia: [
        { label: 'Proprietary Limited Company (Pty Ltd)', value: 'PtyLtd' },
        { label: 'Public Company (Ltd)', value: 'Ltd' },
        { label: 'Partnership', value: 'Partnership' },
        { label: 'Sole Trader', value: 'SoleTrader' }
    ],
    India: [
        { label: 'Private Limited Company (Pvt Ltd)', value: 'PvtLtd' },
        { label: 'Public Limited Company (Ltd)', value: 'Ltd' },
        { label: 'Limited Liability Partnership (LLP)', value: 'LLP' },
        { label: 'Sole Proprietorship', value: 'SoleProprietorship' }
    ],
    China: [
        { label: 'Wholly Foreign-Owned Enterprise (WFOE)', value: 'WFOE' },
        { label: 'Joint Venture (JV)', value: 'JV' },
        { label: 'State-Owned Enterprise (SOE)', value: 'SOE' },
        { label: 'Private Enterprise', value: 'PrivateEnterprise' }
    ],
    Japan: [
        { label: 'Kabushiki Kaisha (KK)', value: 'KK' },
        { label: 'Godo Kaisha (GK)', value: 'GK' },
        { label: 'General Partnership', value: 'GeneralPartnership' },
        { label: 'Limited Partnership', value: 'LimitedPartnership' }
    ],
    Brazil: [
        { label: 'Sociedade Limitada (LTDA)', value: 'LTDA' },
        { label: 'Sociedade Anônima (SA)', value: 'SA' },
        { label: 'Microempreendedor Individual (MEI)', value: 'MEI' },
        { label: 'Empresa Individual de Responsabilidade Limitada (EIRELI)', value: 'EIRELI' }
    ],
    Portugal: [
        { label: 'LDA', value: 'LDA' },
        { label: 'SA', value: 'SA' },
        { label: 'SCP', value: 'SCP' },
        { label: 'ACE', value: 'ACE' }
    ],
    'United Kingdom': [
        { label: 'LTD', value: 'LTD' },
        { label: 'LLP', value: 'LLP' },
        { label: 'PLC', value: 'PLC' }
    ],
    default: [
        { label: 'Corporation', value: 'Corporation' },
        { label: 'LLC', value: 'LLC' },
        { label: 'Partnership', value: 'Partnership' },
        { label: 'Other', value: 'Other' }
    ]
};

const StepOne = ({ handleChange, inputText, isValid }) => {
    const toast = useRef(null);

    const [localInput, setLocalInput] = useState({
        country: inputText.country || '',
        name: inputText.name || '',
        email: inputText.email || '',
        siren: inputText.siren || '',
        companyID: inputText.companyID || '',
        vatNumber: inputText.vatNumber || '',
        legalForm: inputText.legalForm || ''
    });

    const [showFields, setShowFields] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [userClickedNoMatch, setUserClickedNoMatch] = useState(false);

    const [apiFetchedCompany, setApiFetchedCompany] = useState(null);

    const [filteredSuggestions, setFilteredSuggestions] = useState([]);

    useEffect(() => {
        validateInputs(localInput);
        setShowFields(!!localInput.country);
    }, [localInput]);

    const fetchCompanies = async (query) => {
        if (typeof query !== 'string' || !query.trim()) {
            setFilteredSuggestions([]);
            return;
        }
        setLoading(true);

        try {
            const response = await axiosInstance.get('/dashboard/companies/query', {
                params: { name: query }
            });
            if (response.data.results) {
                const formattedSuggestions = response.data.results.map(company => ({
                    label: `${company.nom_complet} - ${company.siege?.adresse || 'N/A'}, ${company.siege?.code_postal || 'N/A'}`,
                    companyName: company.nom_complet || null,
                    companyAdresse: company.siege?.adresse || null,
                    companySiren: company?.siren || null,
                    value: company
                }));
                setFilteredSuggestions(formattedSuggestions);
            } else {
                setFilteredSuggestions([]);
            }
        } catch (err) {
            console.log('No match');
        } finally {
            setLoading(false);
        }
    };

    const debouncedFetchCompanies = useCallback(debounce(fetchCompanies, 300), []);

    const handleLocalChange = (key, value) => {
        const updatedInput = { ...localInput, [key]: value };

        if (key === 'country') {
            const resetInput = { country: value };
            Object.keys(localInput).forEach(k => {
                if (k !== 'country') {
                    resetInput[k] = '';
                }
            });
            setLocalInput(resetInput);
            handleChange(resetInput);
        } else {
            setLocalInput(updatedInput);
            handleChange(updatedInput);
        }

        // If user is typing in "name" AND the selected country is France, we Autocomplete
        if (key === 'name' && localInput.country === 'France') {
            debouncedFetchCompanies(value);
        }
    };

    const selectCompany = (company) => {
        const autoName = company.nom_complet || company.nom_raison_sociale || '';
        const autoEmail = '';
        const autoSiren = company.siren || '';

        // Step2 data
        const step2Data = {
            phone_number: '',
            address: company.siege?.adresse || '',
            city: company.siege?.libelle_commune || '',
            postal_code: company.siege?.code_postal || ''
        };

        const updatedInput = {
            ...localInput,
            name: autoName,
            email: autoEmail,
            siren: autoSiren,
            step2Data
        };
        setLocalInput(updatedInput);
        handleChange(updatedInput);
        setFilteredSuggestions([]);
    };

    const validateInputs = (inputs) => {
        let is_valid = true;

        if (!inputs.country) {
            is_valid = false;
        }

        if (!inputs.name || typeof inputs.name !== 'string' || !inputs.name.trim()) {
            is_valid = false;
        }

        if (!validateEmail(inputs.email)) {
            is_valid = false;
        }

        // If France => SIREN mandatory
        if (inputs.country === 'France') {
            if (!inputs.siren) {
                is_valid = false;
            }
        }

        if (!inputs.legalForm.length) {
            is_valid = false;
        }

        handleChange({
            ...inputs,
            ...(apiFetchedCompany || {})
        });

        isValid(is_valid ? { step: '0' } : false);
    };

    const validateEmail = (email) => {
        if (!email) return false;
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    let allCountries = Object.values(countries.getNames('en', { select: 'official' }));

    allCountries = allCountries.filter(country => country !== 'France');
    allCountries.unshift('France');

    const countryOptions = allCountries.map(c => ({ label: c, value: c }));

    const currentLegalForms = localInput.country
        ? (legalFormsByCountry[localInput.country] || legalFormsByCountry.default)
        : [];

    const displayVATField = localInput.country && isEuropeanCountry(localInput.country);

    const itemTemplate = (suggestion) => {
        return (
            <div className="autocomplete-suggestion mb-1">

                <strong>{suggestion?.companyName}</strong>

                <div className='autocomplete-suggestion-adress pb-0'>
                    <span>{suggestion?.companySiren ? `Siren: ${suggestion?.companySiren}` : ''}</span>
                </div>

                <div className='autocomplete-suggestion-adress pb-0'>
                    <span>{suggestion?.companyAdresse || 'Aucune adresse associé'}</span>
                </div>

            </div>
        );
    };

    return (
        <div>
            <Toast ref={toast} />

            {/* COUNTRY SELECT */}
            <div className="columns">
                <div className="column is-one-third">
                    <label className="label">Pays *</label>
                    <Dropdown
                        value={localInput.country}
                        options={countryOptions}
                        onChange={(e) => handleLocalChange('country', e.value)}
                        placeholder="Choisir un pays"
                        style={{ width: '100%', borderRadius: 4 }}
                        className='primereact-dropdown-less-height'
                        panelClassName='primereact-dropdown-spaced'
                        filter
                    />
                </div>
            </div>

            {showFields && (
                <>
                    {/* NOM DE L'ENTREPRISE */}
                    <div className="columns">
                        <div className="column">
                            <label className="label">Nom de l'entreprise *</label>
                            {localInput.country === 'France' && !userClickedNoMatch ? (
                                <>
                                    <AutoComplete
                                        value={localInput.name}
                                        suggestions={filteredSuggestions}
                                        completeMethod={(e) => debouncedFetchCompanies(e.query)}
                                        field="label"
                                        onChange={(e) => handleLocalChange('name', e.value)}
                                        onSelect={(e) => selectCompany(e.value.value)}
                                        placeholder="Commencez à taper le nom de l'entreprise"
                                        style={{ width: '100%' }}
                                        inputStyle={{ width: '100%', maxHeight: 40, borderRadius: 4 }}
                                        itemTemplate={itemTemplate}
                                    />
                                </>
                            ) : (
                                <input
                                    className="input"
                                    type="text"
                                    placeholder="Nom de l'entreprise"
                                    value={localInput.name}
                                    onChange={(e) => handleLocalChange('name', e.target.value)}
                                />
                            )}
                        </div>

                        <div className="column">
                            <label className="label">Email de l'entreprise *</label>
                            <input
                                className="input"
                                type="email"
                                placeholder="support@entreprise.fr"
                                value={localInput.email}
                                onChange={(e) => handleLocalChange('email', e.target.value)}
                            />
                        </div>
                    </div>

                    {/* SIREN if FR, Company ID otherwise */}
                    {localInput.country === 'France' ? (
                        <div className="columns">
                            <div className="column">
                                <label className="label">SIREN *</label>
                                <input
                                    className="input"
                                    type="text"
                                    placeholder="922075494"
                                    value={localInput.siren}
                                    onChange={(e) => handleLocalChange('siren', e.target.value)}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="columns">
                            <div className="column">
                                <label className="label">Company Identification Number (optionnal)</label>
                                <input
                                    className="input"
                                    type="text"
                                    placeholder="Identification Number"
                                    value={localInput.companyID}
                                    onChange={(e) => handleLocalChange('companyID', e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    {/* Legal Form */}
                    <div className="columns">
                        <div className="column">
                            <label className="label">Forme juridique</label>
                            <Dropdown
                                value={localInput.legalForm}
                                options={currentLegalForms}
                                onChange={(e) => handleLocalChange('legalForm', e.value)}
                                placeholder="Choisir la forme juridique"
                                style={{ width: '100%', borderRadius: 4 }}
                                className='primereact-dropdown-less-height'
                                panelClassName='primereact-dropdown-spaced'
                            />
                        </div>
                    </div>

                    {displayVATField && (
                        <div className="columns">
                            <div className="column">
                                <label className="label">Numéro de TVA (optionnel)</label>
                                <input
                                    className="input"
                                    type="text"
                                    placeholder="FR12345678901"
                                    value={localInput.vatNumber}
                                    onChange={(e) => handleLocalChange('vatNumber', e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default StepOne;